import * as _ from 'underscore';
import * as _flatted from 'flatted';
import { forkJoin, ReplaySubject } from 'rxjs';
import { filter, skip, takeUntil } from 'rxjs/operators';
import { ShopApiService } from '../shop.apiservice';
import { WizardComponent } from 'angular-archwizard';
import { AppService } from '../../../store/app.service';
import { IUser } from '../../../core/models/auth.models';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { IClient } from '../../../core/models/clients.models';
import { getRolesForPage } from '../../../core/helpers/utility';
import { ClientsApiService } from '../../clients/clients.apiservice';
import { UtilityService } from '../../../core/helpers/utility.service';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CART_STEPS, PAGE_NAMES, PRODUCT_FAMILY, PRODUCT_TYPES, TRX_STATUS } from '../../../core/helpers/constants';
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CartStepData, CouponResponse, ICart, ICartData, ICartProductDetails, IProductFinalized, IProductMain, IProductMedia } from '../../../core/models/products.models';

export interface Coupon {
  name: string;
}

@Component({
  selector: 'app-cart-wizard',
  templateUrl: './cart-wizard.component.html',
  styleUrls: ['./cart-wizard.component.scss'],
})

export class CartWizardComponent implements OnInit, OnDestroy {
  @ViewChild(WizardComponent) public wizard!: WizardComponent;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  // Constants
  PRODUCT_TYPES = PRODUCT_TYPES;
  PRODUCT_FAMILY = PRODUCT_FAMILY;
  CART_STEPS = CART_STEPS;

  // Products
  product: IProductMain = {} as IProductMain;
  
  // Strings
  clientEmail: string = '';

  // Forms
  cartForm!: FormGroup;
  campForm!: FormGroup;
  paymentForm: FormGroup;

  roleOptions: any = {};
  
  // Arrays
  clientOwnerOf = new Array<string>();
  clientsData: Array<IClient> = new Array<IClient>();
  selectedClient: IClient = {} as IClient;

  public userRoles: Array<number> = [];
  activeUser: IUser = {} as IUser;

  productAdSpendOptions = new Map<string, Array<number>>();
  finalizedProducts = new Array<IProductFinalized>();

  hasGoogle: boolean = false;
  hasFacebook: boolean = false;
  addingGoogle: boolean = false;
  addingFacebook: boolean = false;
  addingConsulting: boolean = false;
  addingNational: boolean = false;
  readyForCheckout: boolean = false;

  beenHere: boolean = false;
  
  requiredInfoCompleted: boolean = false;
  isNewCampaign: boolean = false;
  isOptionSelected: boolean = false;
  isAdmin: boolean = false;
  isShopManager: boolean = false;

  // readonly separatorKeysCodes = [ENTER, COMMA] as const;

  coupons: Coupon[] = [];
  activeCart: ICart = {} as ICart;
  cartStepData: CartStepData = {} as CartStepData;
  couponResponse: CouponResponse = {} as CouponResponse;

  private campFormOriginalValidators: { [key: string]: any } = {};

  constructor(
    private appService: AppService,
    private route: ActivatedRoute,
    private shopService: ShopApiService,
    private formBuilder: FormBuilder,
    private sanitizer: DomSanitizer,
    private router: Router,
    private clientService: ClientsApiService,
    public uService: UtilityService
  ) {
    this.userRoles = [...this.uService.getUserRoles()];

    this.appService.getActiveUser$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(u => {
        this.activeUser = u;
    });
    
    this.cartForm = this.formBuilder.group({
      cartId: [null],
      productId: [null],
      adSpend: [null],
      couponCode: [null, [Validators.required]],
      couponId: [null],
      stepsData: this.formBuilder.array([])
    });

    this.campForm = this.formBuilder.group({
        campaignId: [null],
        clientId: [null, [Validators.required]],
        brandName: [null, [Validators.required]],
        studioLocationName: [null, [Validators.required]],
        campaignName: [null],
        studioStatus: [null],
        studioEmail: [null, [Validators.required, Validators.email]],
        studioPhone: [null, [Validators.required, Validators.maxLength(20)]],
        studioStreet: [null, [Validators.required]],
        studioCity: [null, [Validators.required]],
        studioState: [null, [Validators.required]],
        studioZipcode: [null, [Validators.required]],
        fbPageUrl: [null],
        crmName: [null],
        crmId: [null],
        notes: [null],
        isFacebook: [null],
        isGoogle: [null]
      });

      this.paymentForm = this.formBuilder.group({
        methodId: [null, [Validators.required]],
        clientId: [null, [Validators.required]],
        nickname: [null],
        firstName: [null],
        lastName: [null, []],
        cardNumber: [null, []],
        cardCVV: [null],
        cardExpiryDate: [null],
        agreeTerms: [null, [Validators.requiredTrue]],
        address: [null, [Validators.required]],
        city: [null, [Validators.required]],
        state: [null, [Validators.required]],
        zip: [null, [Validators.required]],
      });
  }

  ngOnInit(): void {
    if (this.userRoles.includes(9)) {
      this.loadSubscriptionEmployeeClients();
    } else {
      this.appService.getAllClients$()
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: any) => {
          const allClients = response.data.filter((client: any) =>
            !this.clientsData.some((existing: any) => existing.id === client.id)
          );
          this.clientsData = [...this.clientsData, ...allClients];
        });
      this.appService.fetchAllClients({});

      this.appService.getAllProspectClients$()
        .pipe(takeUntil(this.destroyed$)
        )
        .subscribe((response: any) => {
          const allProspects = response.data.filter((client: any) =>
            !this.clientsData.some((existing: any) => existing.id === client.id)
          );
          this.clientsData = [...this.clientsData, ...allProspects];
        });
      this.appService.fetchAllProspectClients({});
    }

    this.appService.getActiveUser$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(u => {
        var r = getRolesForPage(u.roles, PAGE_NAMES.Clients);
        this.isAdmin = r.isAdmin;
        if (this.userRoles.includes(4)) {
          this.isShopManager = true;
        }
        this.clientOwnerOf = [...u.clientOwnerOf];
        if (r.options) {
          this.roleOptions = JSON.parse(r.options!);
        }
      });

    let cartId = this.route.snapshot.paramMap.get('id')!;

    if (cartId) {
      if (this.clientsData.length === 1) {
        this.onChangeClient(this.clientsData[0]);
      }
      this.shopService.getCartDetails(cartId)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((cart: any) => {
          this.activeCart = cart.data;
          if (cart.data.currentStep > 1) {
            this.router.navigate(['/pages/shop']);
          }
          this.appService.clearCartProductDetails$();
          this.appService.fetchCartProductDetails(cart.data.productId);

          this.appService.getCartProductDetails$()
            .pipe(filter(x => !_.isEmpty(x)))
            .subscribe((prod: IProductMain) => {
              this.product = _.clone(prod);
              if (this.product.familyCode !== PRODUCT_FAMILY.Bundle) {
                let pp = _.clone(this.product);
                pp.productsInBundle = [];
                this.product.productsInBundle = [pp];
              }

              if (cart.data.currentStep === CART_STEPS.BundleDetails) {
                // This step is now done in the products list component. Need to load the product details here for the Cart.
                let i = 0;
                let amountsArr = new Array<number>();
                _.sortBy(this.product.productsInBundle, "sortOrder").forEach((p: ICartProductDetails) => {
                  if (p.type === PRODUCT_TYPES.VariableSubscription.code) {
                    amountsArr = p.variableSubscription.map(x => x.amount).sort((n1, n2) => n1 - n2);
                    if (this.product.familyCode === PRODUCT_FAMILY.Bundle) {
                      p.bundleId = this.product.id!;
                      p.bundleName = this.product.name!;
                    }
                    this.productAdSpendOptions.set(p.id, amountsArr);
                  }
                  p.stepCode = this.activeCart.currentStep;
                  (p.media as IProductMedia).src = this.sanitizer.bypassSecurityTrustUrl('data:image/png;base64,' + p.media.blob);
                  this.addStepData(p);
                  this.stepsData.at(i).get('adSpend')?.setValue(amountsArr[0])
                  i++;
                });
                this.onNext(1);
              }
              else if (cart.data.currentStep === CART_STEPS.RequiredInfo) {
                // Location selection or Campaign creation
              }
              else if (cart.data.currentStep === CART_STEPS.Cart) {
                // Ad Spend selection
              }
              else if (cart.data.currentStep === CART_STEPS.Checkout) {
                const cartDataEntry = cart.data.cartData.find((x: any) => x.stepCode === CART_STEPS.Cart);
                let ddArray: any[] = [];
                if (cartDataEntry && cartDataEntry.formData) {
                  try {
                    ddArray = JSON.parse(cartDataEntry.formData);
                  } catch (e) {
                    ddArray = [];
                  }
                }
                if (ddArray[0]) {
                  this.cartStepData = ddArray[0] as CartStepData
                } else {
                  this.cartStepData.subtotal = this.product.productsInBundle.map((x: any) => x.data.price).reduce((s: number, f: number) => {
                    return s + f;
                  }, 0);
                  this.cartStepData.total = this.cartStepData.subtotal;
                }
                if (ddArray[1]) {
                  this.couponResponse = ddArray[1] as CouponResponse
                }
                this.cartForm.patchValue({ couponCode: this.couponResponse.code })
              }
              this.doFinalizeProducts();
            });
          this.cartForm.patchValue({
            cartId: cart.data.id,
            productId: this.activeCart.productId,
          });
        });
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  // NAVIGATION 
  onPrevious(stepCode: number) {
    this.shopService.updateCartStep(this.activeCart.id, stepCode)
      .subscribe((r: any) => {
        if (r.good) {
          this.wizard.goToPreviousStep();
          this.activeCart.currentStep = stepCode;
        }
      });
  }

  continueToCart() {
    if (((!this.hasFacebook) && (!this.hasGoogle)) || (!this.addingFacebook && !this.addingGoogle)) {
      window.scrollTo(0, 0);
      this.onNext(CART_STEPS.Cart);
      this.wizard.goToNextStep();
    }
  }
  
  onNext(stepCode: number) {
    let thisStepData = this.activeCart.cartData.find(x => x.stepCode == stepCode)!

    if (stepCode === CART_STEPS.BundleDetails) {
      // Product Selection (save and pass through)
      let formData = this.activeCart;
      thisStepData.formData = JSON.stringify(this.stepsData.value);
      this.shopService.saveCart(formData)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((x: any) => {
          if (x.good) {
            this.wizard.goToNextStep();
            this.activeCart.currentStep = stepCode + 1;
          }
        })
    }
    else if ((stepCode === CART_STEPS.RequiredInfo)) {
      // Location selection or Campaign creation
      if (this.isNewCampaign) {
        this.campForm.controls["campaignId"].setValue(null);
      }

      if (this.campForm.valid) {
        // Save Cart Data
        this.activeCart.currentStep = CART_STEPS.RequiredInfo;
        if (thisStepData === undefined) {
          this.activeCart.cartData.push({
            stepCode: CART_STEPS.RequiredInfo,
            formData: JSON.stringify(this.campForm.value)
          } as ICartData)
        }
        else {
          thisStepData.formData = JSON.stringify(this.campForm.value);
        }
        this.shopService.saveCart(this.activeCart)
          .pipe(takeUntil(this.destroyed$))
          .subscribe((x: any) => {
            if (x.good) {
              this.wizard.goToNextStep();
              this.activeCart.currentStep = stepCode + 1;
            }
          })
      }
    }
    else if (stepCode === CART_STEPS.Cart) {
      // Ad Spend selection completed
      this.activeCart.currentStep = CART_STEPS.Cart;
      let infoFormData = [this.cartStepData, this.couponResponse];
      if (thisStepData === undefined) {
        this.activeCart.cartData.push({
          stepCode: CART_STEPS.Cart,
          formData: JSON.stringify(infoFormData)
        } as ICartData);
        this.onNext(CART_STEPS.Cart);
      } else {
        thisStepData.formData = JSON.stringify(infoFormData);
        this.shopService.saveCart(this.activeCart)
          .pipe(takeUntil(this.destroyed$))
          .subscribe((x: any) => {
            if (x.good) {
            }
          });
        }
    }
    else if (stepCode === CART_STEPS.Checkout) {
      this.clientService.getClientDetails(this.paymentForm.controls["clientId"].value)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: any) => {
          this.clientEmail = response.data.email;
        });

      if (this.readyForCheckout) {
        this.activeCart.currentStep = CART_STEPS.Checkout;
        // right here - gotta change the order amount depending on the product
        let formData = [{ ...this.getPaymentFormValues(), orderAmount: this.cartStepData.total.toFixed(2), ccProcessingFee: (this.cartStepData.ccProcessingFee ?? 0).toFixed(2) }];
        if (thisStepData === undefined) {
          // turn around if invalid
          if (this.finalizedProducts[0].type !== PRODUCT_FAMILY.Bundle) {
            this.finalizedProducts = [...this.finalizedProducts];
          }
          this.finalizedProducts.map(x => { x.media.blob = '', x.media.src = '' });
          const appliedCouponId = (this.couponResponse.couponApplied && this.couponResponse.couponApplied === true && this.couponResponse.id)
            ? this.couponResponse.id
            : null;

          this.activeCart.cartData.push({
            stepCode: CART_STEPS.Checkout,
            formData: JSON.stringify(formData),
            productsData: JSON.stringify(this.finalizedProducts),
            couponId: appliedCouponId
          } as ICartData);
        } else {
          const appliedCouponId = (this.couponResponse.couponApplied && this.couponResponse.couponApplied === true && this.couponResponse.id)
            ? this.couponResponse.id
            : null;

          thisStepData.formData = JSON.stringify(formData);
          thisStepData.productsData = JSON.stringify(this.finalizedProducts);
          thisStepData.couponId = appliedCouponId;
        }
        this.shopService.saveCart(this.activeCart)
          .pipe(takeUntil(this.destroyed$))
          .subscribe((x: any) => {
            if (x.good) {
              if (x.data.response.transactionStatus === TRX_STATUS.TransactionApproved) {
                this.router.navigate(['/pages/shop/ordercomplete', 1, x.data.response.orderNumber, x.data.response.orderId, this.clientEmail, this.getRecurringDate()]);
              } else if (x.data.response.transactionStatus === TRX_STATUS.TransactionDeclined || x.data.response.transactionStatus === TRX_STATUS.Error ) {
                this.router.navigate(['/pages/shop/ordercomplete', 0, x.data.response.orderNumber, x.data.response.orderId,]);
              }
            }
          });
      }
    }
  }

  async onStepEnter(evt: any, stepCode: number) {
    if (this.activeCart && this.activeCart.currentStep && this.activeCart.currentStep !== stepCode) {
      this.shopService.updateCartStep(this.activeCart.id, stepCode)
        .subscribe((r: any) => {
          if (r.good) {
            this.activeCart.currentStep = stepCode;
          }
        });
    }
    if (stepCode === CART_STEPS.BundleDetails) {
      // Product Selection
      if (this.beenHere) {
        this.router.navigate(['/pages/shop']);
      }
      this.beenHere = true;
    } else if (stepCode === CART_STEPS.RequiredInfo) {
      // Select or Create Campaign
      window.scrollTo(0, 0);
      await this.checkAddingPlatforms();
      this.resetCampaignInfo();
    } else if (stepCode === CART_STEPS.Cart) {
      // Ad Spend selection
      window.scrollTo(0, 0);
      await this.checkAddingPlatforms();
      this.resetAdSpend();
    }
  }

  public get stepsData(): FormArray {
    return this.cartForm.get('stepsData') as FormArray
  }

  getRecurringDate(): any {
    var recurringDate = this.finalizedProducts.find(x => x.type === PRODUCT_TYPES.SimpleSubscription.code || x.type === PRODUCT_TYPES.VariableSubscription.code)?.recurringStartDate;
    if (recurringDate === undefined) {
      recurringDate = '';
    }
    return recurringDate;
  }

  addStepData(cartProduct: ICartProductDetails) {
    const newStepData = this.formBuilder.group({
      id: cartProduct.id,
      signupFee: cartProduct.data.signupFee,
      display: cartProduct.display,
      familyCode: cartProduct.familyCode,
      description: cartProduct.description,
      stepCode: cartProduct.stepCode,
      type: cartProduct.type,
      mediaUrl: cartProduct.media.src,
      required: false,
      duration: cartProduct.data.duration,
      includeProduct: [true],
      isGoogle: cartProduct.isGoogle,
      isFacebook: cartProduct.isFacebook,
      price: cartProduct.data.price,
      frequency: cartProduct.data.frequency,
      bundleId: cartProduct.bundleId,
      bundleName: cartProduct.bundleName,
      isRequired: cartProduct.isRequired,
      adSpend: [1],
    })
    this.stepsData.push(newStepData);
  }

  getPaymentFormValues() {
    if (!this.paymentForm.invalid)
      return this.paymentForm.value;
  }

  private productRequiresRequiredInfo(product: IProductFinalized): boolean {
    return [
      PRODUCT_FAMILY.DigitalMarketing,
      PRODUCT_FAMILY.CampaignConsulting,
      PRODUCT_FAMILY.AdSpend,
      PRODUCT_FAMILY.AddPlatform,
    ].includes(product.familyCode);
  }

  private refreshPlatformSelections(): void {
    this.addingGoogle = this.finalizedProducts.some(product => product.display?.includes('Google'));
    this.addingFacebook = this.finalizedProducts.some(product => product.display?.includes('Meta'));
    this.addingConsulting = this.finalizedProducts.some(product => product.display?.includes('Consulting'));
    this.addingNational = this.finalizedProducts.some(product => product.display?.includes('National'));
  }

  // Products
  doFinalizeProducts() {
    this.finalizedProducts = [];
    for (let ctrl of this.stepsData.controls) {
      let id = (ctrl as FormGroup).controls.id.value;
      let include = (ctrl as FormGroup).controls.includeProduct?.value;
      if (include) {
        let fProduct = this.product.productsInBundle.find(x => x.id === id) as IProductFinalized;
        if (fProduct !== undefined) {
          if (fProduct.bundleId === undefined) {
            fProduct.bundleId = '';
          }
          if ((fProduct.type !== undefined) && (fProduct.type === PRODUCT_TYPES.VariableSubscription.code)) {
            fProduct.adSpend = (ctrl as FormGroup).controls.adSpend?.value;
            if (typeof (fProduct.adSpend) === 'string') {
              fProduct.adSpend = parseInt(fProduct.adSpend);
            }
          }
          if ((this.product !== undefined) && (this.product.familyCode === PRODUCT_FAMILY.Bundle)) {
            if (this.product !== undefined) {
              if (this.product.id !== undefined) {
                fProduct.bundleId = this.product.id;
              }
              if (this.product.name !== undefined) {
                fProduct.bundleName = this.product.name;
              }
            }
          }
          this.finalizedProducts.push(fProduct);
        }
      }
    }

    this.refreshPlatformSelections();
    this.requiredInfoCompleted = this.finalizedProducts.length > 0
      && this.finalizedProducts.every(product => !this.productRequiresRequiredInfo(product));
  }

  resetCampaignInfo() {
    // Restore original validators for campForm fields
    Object.keys(this.campForm.controls).forEach(key => {
      this.campForm.controls[key].setValidators(this.campFormOriginalValidators[key]);
      this.campForm.controls[key].updateValueAndValidity();
    });
    
    this.isOptionSelected = false;
    this.requiredInfoCompleted = false;
    this.isNewCampaign = false;
    this.campForm.controls["clientId"].setValue('');
    this.paymentForm.controls["clientId"].setValue('');
    this.hasGoogle = false;
    this.hasFacebook = false;
  }

  resetAdSpend() {
    this.paymentForm.patchValue({
      methodId: null,
      address: null,
      city: null,
      state: null,
      zip: null,
    });
  }

  async checkAddingPlatforms() {
    this.refreshPlatformSelections();
  }

  onSwitchChange(evt: any, index: number) {
    this.stepsData.at(index).get('includeProduct')?.setValue(evt);
    this.doFinalizeProducts();
  }

  placeOrder(cart: any, coupon: any) {
    this.cartStepData = cart;
    this.couponResponse = coupon;
    this.readyForCheckout = true;
    this.onNext(CART_STEPS.Cart); 
    this.onNext(CART_STEPS.Checkout);
  }

  // Existing Campaign Selected
  selectedCampaign(clientId: string, campaign: Map<string, string>, campaignId: string) {
    this.isNewCampaign = false;
    this.campForm.controls["campaignId"].setValue(campaignId)
    this.campForm.controls["clientId"].setValue(clientId);
    this.campForm.controls['campaignName'].setValue(campaign.get('name'));
    this.paymentForm.controls["clientId"].setValue(clientId);

    // Only require campaignId and clientId for this run
    Object.keys(this.campForm.controls).forEach(key => {
      if (key === 'campaignId' || key === 'clientId') {
        this.campForm.controls[key].setValidators(Validators.required);
      } else {
        this.campForm.controls[key].clearValidators();
      }
      this.campForm.controls[key].updateValueAndValidity();
    });

    this.onNext(CART_STEPS.RequiredInfo);
  }

  onChangeClient(client: IClient) {
    this.selectedClient = client;
  }

  onNewCampaign(isNew: boolean = false) {
    this.isNewCampaign = isNew;
    this.isOptionSelected = isNew;
  }

  onCampFormSubmit(campFormData: FormGroup) {
    this.campForm = campFormData; 
    this.paymentForm.controls["clientId"].setValue(campFormData.value.clientId);
    this.onNext(CART_STEPS.RequiredInfo); 
  }

  private loadSubscriptionEmployeeClients(): void {
    this.appService.getAllCampaigns$()
      .pipe(skip(1), takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        const clientIds: string[] = Array.from(new Set<string>((response?.data || []).map((campaign: any) => campaign.clientId).filter(Boolean)));

        if (!clientIds.length) {
          this.clientsData = [];
          return;
        }

        forkJoin(clientIds.map((clientId: string) => this.clientService.getClientDetails(clientId).pipe(takeUntil(this.destroyed$))))
          .pipe(takeUntil(this.destroyed$))
          .subscribe((clientResponses: any[]) => {
            this.clientsData = Array.from(new Map<string, IClient>(
              clientResponses
                .map((clientResponse: any) => clientResponse?.data as IClient)
                .filter((client: IClient | undefined) => !!client)
                .map((client: IClient) => [client.id, client] as [string, IClient])
            ).values());
          });
      });

    this.appService.fetchAllCampaigns({});
  }
}
